/* @flow */
// Libs
import { complement } from 'ramda';
import { assertArray } from '@braindate/util/lib/assert';

// Utils
import { getBraindateConversation } from '@braindate/domain/lib/braindate/util';
import { isGroupTopic, getTopicAuthor } from '@braindate/domain/lib/topic/util';
import { getConversationOtherParticipants } from 'src/shared/domain/conversation/util/conversationUtil';
import { getExpressionOfGratitudeSender } from '@braindate/domain/lib/expressionOfGratitude/util/expressionOfGratitudeGetters';
import {
  getPastActiveBraindates,
  getConfirmedActiveBraindates,
  getCancelledBraindates,
  getBraindatesToCheckIn,
  getPendingBraindateInvitations,
  getAwaitingBraindateInvitations,
  getDeclinedBraindateInvitations,
  sortActiveBraindatesByMostRecent,
  sortBraindatesByMostRecentActivity,
  isBraindateCheckInReady,
  getBraindateTopic,
  getLiveActiveBraindates,
} from 'src/shared/domain/braindate/util/braindateUtil';

// Types
import type { User } from '@braindate/domain/lib/user/type';
import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { ExpressionOfGratitude } from '@braindate/domain/lib/expressionOfGratitude/type';

export function filterConfirmedBraindates(
  braindates: Array<Braindate>,
  isSelfCheckInEnabled?: boolean = false,
): Array<Braindate> {
  assertArray(braindates, 'braindates');

  if (!braindates.length) return [];

  const liveBraindates = getLiveActiveBraindates(braindates);
  let confirmedBraindates = getConfirmedActiveBraindates(braindates);
  let braindatesToCheckIn = [];

  if (isSelfCheckInEnabled) {
    braindatesToCheckIn = getBraindatesToCheckIn(braindates);
    confirmedBraindates = confirmedBraindates.filter(
      complement(isBraindateCheckInReady),
    );
  }

  const result = [
    ...liveBraindates,
    ...braindatesToCheckIn,
    ...confirmedBraindates,
  ];

  sortActiveBraindatesByMostRecent(result);

  return result;
}

export function filterPendingBraindates(
  braindates: Array<Braindate>,
  user: ?User,
): Array<Braindate> {
  assertArray(braindates, 'braindates');

  if (!braindates.length || !user) return [];

  const receivedInvitations = getPendingBraindateInvitations(braindates, user);
  const sentInvitations = getAwaitingBraindateInvitations(braindates, user);
  const declinedInvitations = getDeclinedBraindateInvitations(braindates);

  const result = [...receivedInvitations, ...sentInvitations];

  sortBraindatesByMostRecentActivity(result);
  sortBraindatesByMostRecentActivity(declinedInvitations);

  return [...result, ...declinedInvitations];
}

export function filterPastBraindates(
  braindates: Array<Braindate>,
): Array<Braindate> {
  assertArray(braindates, 'braindates');

  if (!braindates.length) return [];

  const pastBraindates = getPastActiveBraindates(braindates);
  const cancelledBraindates = getCancelledBraindates(braindates);

  sortBraindatesByMostRecentActivity(pastBraindates);
  sortBraindatesByMostRecentActivity(cancelledBraindates);

  return pastBraindates;
}

export function filterCancelledBraindates(
  braindates: Array<Braindate>,
): Array<Braindate> {
  assertArray(braindates, 'braindates');

  if (!braindates.length) return [];

  const pastBraindates = getPastActiveBraindates(braindates, true);
  const cancelledBraindates = getCancelledBraindates(braindates);

  sortBraindatesByMostRecentActivity(pastBraindates);
  sortBraindatesByMostRecentActivity(cancelledBraindates);

  return cancelledBraindates;
}

export function getBraindateWith(
  self: User,
  braindate: Braindate,
  expressionOfGratitude?: ExpressionOfGratitude,
) {
  const topic = getBraindateTopic(braindate);
  const isGroupBraindate = isGroupTopic(topic);
  const conversation = getBraindateConversation(braindate);

  if (isGroupBraindate) return getTopicAuthor(topic);

  if (expressionOfGratitude) {
    return getExpressionOfGratitudeSender(expressionOfGratitude);
  }

  const otherParticipants = self
    ? getConversationOtherParticipants(conversation, self)
    : [];

  return otherParticipants[0];
}
